import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import {
  stylePanelBeforeCloseRef,
  stylePanelSaveAndCloseRef,
  stylePanelDiscardAndCloseRef,
  pendingStylePanelActionRef,
  setPendingStylePanelAction,
  STYLE_PANEL_UNSAVED_TOAST,
} from '@/utils/stylePanelGuard';
import { setActivePanel } from '@/store/assetsPanel/assetsPanelSlice';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StylePreview } from '@/components/panels/styles/StylePreview/StylePreview';
import { StylesGeneralSettings } from '@/components/panels/styles/StylesGeneralSettings/StylesGeneralSettings';
import { StylesTextStyle } from '@/components/panels/styles/StylesTextStyle/StylesTextStyle';
import { StylesButtonStyle } from '@/components/panels/styles/StylesButtonStyle/StylesButtonStyle';
import { StylesColorStyles } from '@/components/panels/styles/StylesColorStyles/StylesColorStyles';
import type {
  StyleButtons,
  StyleColors,
  StylesConfig,
  StyleTypography,
} from '@builder4/shared/types/styles';
import type { CreateUpdateGlobalStyleRequest, GlobalStyle } from '@/store/globalStyles/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createGlobalStyle, updateGlobalStyleById } from '@/store/globalStyles/thunks';
import {
  selectActiveGlobalStyleId,
  selectCreateGlobalStyleLoading,
  selectUpdateGlobalStyleLoading,
} from '@/store/globalStyles/selectors';
import { ASSETS_PANEL } from '@builder4/shared/utils/constants';
import {
  setActiveGlobalStyle,
  setEditingStyleColors,
  clearEditingStyleColors,
} from '@/store/globalStyles/slice';
import { defaultStyleConfig } from '@builder4/shared/styles/defaultStyle';
import type { ButtonSize } from '@builder4/shared/styles/constants';
import { validateStylesConfig } from '@builder4/shared/dto/styles';
import { showToast } from '@/store/notifications/slice';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT } from '@/analytics/mixpanel';
import { captureSentryException } from '@/utils/sentry';

export interface StylesEditStyleProps {
  className: string;
  onStylesClick?: (panel: string) => void;
  /** Optional height override for the inner ScrollArea; useful for Storybook Docs. */
  scrollHeight?: number | string;
  initialData: GlobalStyle | null;
  mode?: 'edit' | 'create';
  isBrandProfileStyle?: boolean;
}

const cloneStylesConfig = (config: StylesConfig): StylesConfig =>
  JSON.parse(JSON.stringify(config)) as StylesConfig;

function stylePayloadEqual(
  a: { label: string; global_style: StylesConfig },
  b: { label: string; global_style: StylesConfig },
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const StylesEditStyle: React.FC<StylesEditStyleProps> = ({
  className,
  onStylesClick,
  initialData,
  mode,
  isBrandProfileStyle,
}) => {
  const dispatch = useAppDispatch();
  const creationLoading = useAppSelector(selectCreateGlobalStyleLoading);
  const updateLoading = useAppSelector(selectUpdateGlobalStyleLoading);
  const track = useMixpanel();

  const handleClick = useCallback((panel: string) => onStylesClick?.(panel), [onStylesClick]);

  const activeStyleId = useAppSelector(selectActiveGlobalStyleId);

  const resolvedMode = initialData?.id ? (mode ?? 'edit') : 'create';
  const isEditMode = resolvedMode === 'edit';

  const baselineRef = useRef<{ label: string; global_style: StylesConfig }>({
    label: initialData?.label ?? '',
    global_style: cloneStylesConfig(initialData?.globalStyle ?? defaultStyleConfig),
  });

  // Initialize state from initialData
  const [globalStyleData, setGlobalStyleData] = useState<StylesConfig>(
    cloneStylesConfig(initialData?.globalStyle ?? defaultStyleConfig),
  );
  const [generalData, setGeneralData] = useState<{ label: string }>({
    label: initialData?.label ?? '',
  });
  const [editStyleId, setEditStyleId] = useState<string>(initialData?.id ?? '');
  const [selectedButtonSize, setSelectedButtonSize] = useState<ButtonSize>('medium');

  // Update state when initialData changes
  useEffect(() => {
    if (initialData) {
      setGlobalStyleData(cloneStylesConfig(initialData.globalStyle));
      setGeneralData({ label: initialData.label });
      setEditStyleId(isEditMode ? initialData.id : '');
      baselineRef.current = {
        label: initialData.label,
        global_style: cloneStylesConfig(initialData.globalStyle),
      };
    } else {
      setGlobalStyleData(cloneStylesConfig(defaultStyleConfig));
      setGeneralData({ label: '' });
      setEditStyleId('');
      baselineRef.current = {
        label: '',
        global_style: cloneStylesConfig(defaultStyleConfig),
      };
    }
  }, [initialData, isEditMode]);

  const getHasUnsavedChanges = useCallback((): boolean => {
    const current = {
      label: generalData.label,
      global_style: globalStyleData,
    };
    return !stylePayloadEqual(baselineRef.current, current);
  }, [generalData, globalStyleData]);

  useEffect(() => {
    stylePanelBeforeCloseRef.current = getHasUnsavedChanges;
    return () => {
      stylePanelBeforeCloseRef.current = null;
    };
  }, [getHasUnsavedChanges]);

  const handleRequestCloseOrBack = useCallback(
    (panel: string) => {
      if (getHasUnsavedChanges()) {
        setPendingStylePanelAction(panel);
        dispatch(showToast(STYLE_PANEL_UNSAVED_TOAST));
        return;
      }
      handleClick(panel);
    },
    [getHasUnsavedChanges, dispatch, handleClick],
  );
  // Sync editing style colors to Redux so style editors (e.g. button) can read palette without prop drilling.
  useEffect(() => {
    dispatch(setEditingStyleColors(globalStyleData.colors));
    return () => {
      dispatch(clearEditingStyleColors());
    };
  }, [dispatch, globalStyleData.colors]);

  const title = isEditMode ? (isBrandProfileStyle ? 'Edit Brand Kit' : 'Edit Style') : 'New Style';
  const saveButtonText = isEditMode ? 'Save' : 'Add';

  const onGeneralDataChange = useCallback((data: { label: string }) => {
    setGeneralData(data);
  }, []);

  const onTextDataChange = useCallback((data: StyleTypography) => {
    setGlobalStyleData((prev) => ({ ...prev, typography: data }));
  }, []);

  const onColorDataChange = useCallback((data: StyleColors) => {
    setGlobalStyleData((prev) => ({ ...prev, colors: data }));
  }, []);

  const onButtonDataChange = useCallback((data: StyleButtons) => {
    setGlobalStyleData((prev) => ({ ...prev, buttons: data as StylesConfig['buttons'] }));
  }, []);

  const getCurrentStyleData = useCallback((): CreateUpdateGlobalStyleRequest => {
    return {
      label: generalData.label,
      global_style: globalStyleData,
      isDuplicate: false,
    };
  }, [generalData, globalStyleData]);

  const handleOnSave = async (options?: { skipNavigate?: boolean }): Promise<boolean> => {
    const dataToSave = getCurrentStyleData();

    if (!dataToSave.label.trim()) {
      dataToSave.label = `Untitled Style`;
      setGeneralData({ label: dataToSave.label });
    }

    const result = validateStylesConfig(dataToSave.global_style);
    if (!result.isValid) {
      const error = new Error('Invalid global styles configuration');
      captureSentryException(error, {
        tags: { feature: 'global-styles', operation: 'validation' },
        extra: {
          validationErrors: result.errorMessages,
          styleId: editStyleId,
          isEditMode,
        },
        level: 'error',
      });
      console.error(`Invalid style configuration: ${result.errorMessages}`);
      dispatch(
        showToast({
          text: 'Invalid style configuration',
          state: 'error',
        }),
      );
      return false;
    }

    try {
      if (isEditMode) {
        const result = await dispatch(
          updateGlobalStyleById({
            styleId: editStyleId,
            body: dataToSave,
          }),
        ).unwrap();
        if (result.id === activeStyleId) {
          dispatch(setActiveGlobalStyle(result));
        }
        baselineRef.current = {
          label: dataToSave.label,
          global_style: dataToSave.global_style,
        };
        track(EVENT.PAGE_STYLES_EDITED);
      } else {
        await dispatch(createGlobalStyle(dataToSave)).unwrap();
        baselineRef.current = {
          label: dataToSave.label,
          global_style: dataToSave.global_style,
        };
        track(EVENT.PAGE_STYLES_EDITED);

        if (!options?.skipNavigate) {
          handleClick(ASSETS_PANEL.STYLES);
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleOnSaveRef = useRef(handleOnSave);
  handleOnSaveRef.current = handleOnSave;

  useEffect(() => {
    stylePanelSaveAndCloseRef.current = async () => {
      const saved = await handleOnSaveRef.current?.({ skipNavigate: true });
      if (saved) {
        dispatch(setActivePanel(pendingStylePanelActionRef.current || ''));
        pendingStylePanelActionRef.current = '';
      }
    };
    stylePanelDiscardAndCloseRef.current = () => {
      dispatch(setActivePanel(pendingStylePanelActionRef.current || ''));
      pendingStylePanelActionRef.current = '';
    };
    return () => {
      stylePanelSaveAndCloseRef.current = null;
      stylePanelDiscardAndCloseRef.current = null;
    };
  }, [dispatch]);

  // Close all open style inspectors and color pickers when the panel scrolls
  // But NOT when scrolling inside the inspector dropdown itself
  const handleScrollClose = useCallback((e: React.UIEvent) => {
    // Check if the scroll originated from inside a heading dropdown portal
    const target = e.target as HTMLElement;
    if (target.closest('[data-heading]')) {
      // Scrolling inside a heading inspector - don't close it
      return;
    }
    // Also check if there's a heading dropdown currently visible in the body
    const openDropdown = document.body.querySelector('[data-heading]');
    if (openDropdown) {
      // A heading dropdown is open - don't close it on scroll
      return;
    }
    try {
      // Close all inspector dropdowns
      window.dispatchEvent(
        new CustomEvent('lp:styles-edit-open', { detail: { id: '__scroll_close__' } }),
      );
    } catch {
      /* noop */
    }
    try {
      // Close all color pickers
      window.dispatchEvent(
        new CustomEvent('lp:colorpicker-open', { detail: { id: '__scroll_close__' } }),
      );
    } catch {
      /* noop */
    }
  }, []);

  return (
    <div className={clsx('bg-neutral-0 flex h-full flex-col', className, 'w-[280px]')}>
      <PanelTopHeader
        text={title}
        backButton={true}
        onClickBtnBack={() => handleRequestCloseOrBack(ASSETS_PANEL.STYLES)}
        onClickClose={() => handleRequestCloseOrBack('')}
        onSaveClick={handleOnSave}
        className={''}
        saveBtnShow={true}
        saveBtnText={saveButtonText}
        saveBtnState={
          creationLoading || updateLoading
            ? 'loading'
            : generalData.label.trim() === ''
              ? 'disabled'
              : 'default'
        }
      />

      <ScrollArea className="flex-1 overflow-hidden" onScrollCapture={handleScrollClose}>
        <div className="flex w-full flex-col gap-3 p-6">
          {/* Preview */}
          <div className="flex w-full flex-col gap-2 pb-3">
            <p className="font-medium text-neutral-500" style={{ fontSize: 10 }}>
              Preview
            </p>
            <StylePreview styles={globalStyleData} buttonSize={selectedButtonSize} />
          </div>

          {/* Sections */}
          <div className="mt-1 flex w-full flex-col items-start">
            <StylesGeneralSettings
              className="w-full"
              isOpenCollapse={true}
              onDataChange={onGeneralDataChange}
              value={generalData.label}
              isReadOnly={isBrandProfileStyle}
            />
            <StylesTextStyle
              className="w-full"
              isOpenCollapse={false}
              onDataChange={onTextDataChange}
              value={globalStyleData.typography}
            />
            <StylesColorStyles
              className="w-full"
              isOpenCollapse={false}
              onDataChange={onColorDataChange}
              value={globalStyleData.colors}
            />
            <StylesButtonStyle
              className="w-full"
              isOpenCollapse={false}
              onDataChange={onButtonDataChange}
              value={globalStyleData.buttons}
              onButtonSizeChange={setSelectedButtonSize}
            />
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
